import { ChangeDetectorRef, Component, inject, OnInit, signal, Type } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';

import { first, tap } from 'rxjs';
import { AdminService } from '../../../admin/services/admin.service';
import { ConfirmDialogComponent } from '../../../main/confirm-dialog/confirm-dialog.component';
import { InventoryAction } from '../../../models/inventory_action.model';
import { Status } from '../../../models/status.model';
import { InventoryActionService } from '../../../services/inventory-action.service';
import { StatusesService } from '../../../services/statuses.service';
import { toUnderscore } from '../../../utils/string-utils';
import { ActivatedRoute } from '@angular/router';
import { InventoryActionForPricingDetailsComponent } from './inventory-action-for-pricing-details/inventory-action-for-pricing-details.component';
import { InventoryActionForBuyOrderDetailsComponent } from './inventory-action-for-buy-order-details/inventory-action-for-buy-order-details.component';
import { InventoryActionForMovementDetailsComponent } from './inventory-action-for-movement-details/inventory-action-for-movement-details.component';
import { Location } from '@angular/common';
import { InventoryActionProduct } from '../../../models/inventory-action-product';
import { ChatService } from '../../../chat/chat.service';

const COMPONENT_MAP: Record<string, Type<any>> = {
  'InventoryAction::Pricing': InventoryActionForPricingDetailsComponent,
  'InventoryAction::BuyOrder': InventoryActionForBuyOrderDetailsComponent,
  'InventoryAction::Movement': InventoryActionForMovementDetailsComponent,
};

@Component({
    selector: 'app-inventory-action-details',
    templateUrl: './inventory-action-details.component.html',
    styleUrls: ['./inventory-action-details.component.scss'],
    standalone: false
})
export class InventoryActionDetailsComponent implements OnInit {
  displayedColumns: string[] = ['product_id', 'quantity'];

  inventoryAction: InventoryAction;

  statuses: Status[];

  loading = signal<boolean>(false);

  private readonly activatedRoute = inject(ActivatedRoute);

  private readonly cdr = inject(ChangeDetectorRef);

  public readonly adminService = inject(AdminService);

  protected readonly inventoryActionService = inject(InventoryActionService);

  private readonly statusService = inject(StatusesService);

  private readonly dialog = inject(MatDialog);

  public readonly dialogRef = inject(MatDialogRef<InventoryActionDetailsComponent>);

  public readonly data = inject(MAT_DIALOG_DATA);

  private readonly location = inject(Location);

  private readonly chatService = inject(ChatService);

  constructor() {}

  get inventoryDetailsComponent(): Type<any> | null {
    return COMPONENT_MAP[this.inventoryAction?.type] || null;
  }

  get inventoryActionProducts() : InventoryActionProduct[] {
    return this.inventoryAction?.inventory_action_products?.filter(iap => iap.inventoriable) || [];
  }

  ngOnInit(): void {
    if (this.data?.inventoryAction) {
      this.inventoryAction = this.data.inventoryAction;
      this.openDetails(this.inventoryAction.id);
    } else {
      this.activatedRoute.params.subscribe((params) => {
        if (params.id) {
          this.openDetails(params.id);
        }
      });
    }
  }

  setStatus(status_id: number) {
    this.dialog
      .open(ConfirmDialogComponent, { data: { title: 'Actualizar Estado' } })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.inventoryAction.status_id = status_id;
          this.updateInventoryAction({ status_id });
        }
      });
  }

  openDetails(id: number) {
    this.loading.set(true);
    this.inventoryActionService.get(id).subscribe((response) => {
      this.cdr.markForCheck();
      this.loading.set(false);
      this.inventoryAction = response;
      this.statusService.statuses('Status::' + this.inventoryAction.type).subscribe((statuses) => {
        this.cdr.markForCheck();
        this.statuses = statuses;
      });

      if (!this.inventoryDetailsComponent) {
        return;
      }
    });
  }

  updateInventoryAction(changes: Partial<InventoryAction>) {
    this.loading.set(true);
    this.inventoryActionService
      .update(this.inventoryAction.id, changes).pipe(
        tap((response) => Object.assign(this.inventoryAction, response)),
      )
      .subscribe(() => {
        this.loading.set(false);
        setTimeout(() => { this.updateWorks(); }, 1000);
      });
  }

  goBack(){
    if (this.dialogRef instanceof MatDialogRef) {
      this.dialogRef.close();
    } else {
      this.location.back();
    }
  }
  addChat() {
    this.chatService.addChat(this.inventoryAction.type, this.inventoryAction.id).pipe(first()).subscribe({
      next: () => {
        if (this.dialogRef instanceof MatDialogRef) {
          this.dialogRef.close();
        }
      },
      error: (error: any) => {
        console.error('Error adding chat', error);
      }
    });
  }

  private updateWorks() {
    this.inventoryActionService.getWorks(this.inventoryAction.id).subscribe((response) => {
      Object.assign(this.inventoryAction, { ...this.inventoryAction, works: response });
    });
  }

  reloadInventoryAction() {
    this.loading.set(true);
    this.inventoryActionService.get(this.inventoryAction.id).subscribe((response) => {
      this.cdr.markForCheck();
      this.loading.set(false);
      Object.assign(this.inventoryAction, response);
    });
  }

  get title() {
    const action = this.adminService.getText(toUnderscore(this.inventoryAction?.type));
    return `${action} #${this.inventoryAction?.correlative_id}`;
  }
}